import React, { useState, useCallback, useRef, useMemo } from "react";
import styled from "styled-components";
import {
  colors2,
  InlineAlert,
  UI_BODY_SM_DF,
  UI_CAPTION_SEMIBOLD_DF,
} from "oolib";
import {
  globalInputElemBaseBorderStyling_v2,
  globalInputElemHover_v2,
  globalInputElemFocused_v2,
  globalInputElemDisabledStyling_v2,
  globalInputElemTextColor,
  globalInputElemPlaceholderColor,
  globalInputElemPadding_v2,
} from "oolib/dist/v2/themes/globalStyles";
import { ButtonGhost, ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import { AnimatePresence, motion } from "framer-motion";

const InputWithActionContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.6rem 0;
  isolation: isolate;

  ${globalInputElemBaseBorderStyling_v2};
  ${globalInputElemHover_v2};
  ${globalInputElemFocused_v2};
  ${globalInputElemDisabledStyling_v2};
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;

  ${globalInputElemTextColor};
  padding: 0 1rem;
  /* ${globalInputElemPadding_v2}; */

  &::placeholder {
    ${globalInputElemPlaceholderColor};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  padding-right: 0.6rem;
  margin-left: 0.6rem;
  flex-shrink: 0;
`;

const StyledErrorOuterWrapper = styled.div`
  background: ${({ hasError }) => (hasError ? colors2.redBG : "transparent")};
  border-radius: 0.6rem;
`;

const StyledErrorContentWrapper = styled(motion.div)`
  overflow: hidden;
`;

const StyledErrorContent = styled.div`
  padding-block: 0.4rem;
  padding-inline: 1rem;
  color: ${colors2.redText};
`;

/**
 *
 * @param {Object} props
 * @param {string} props.value - Controlled input value
 * @param {Function} props.onChange - Callback when input value changes (value, id)
 * @param {Function} props.onAction - Callback when action button is clicked or Enter is pressed (value)
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.id - Input ID
 * @param {string} props.actionButtonIcon - Icon for the action button
 * @param {string} props.actionButtonLabel - Label for the action button
 * @param {Object} props.inputProps - Additional props to pass to the input element
 * @param {Function} props.validateInput - Optional validation function (value) => errorMessage or null
 * @param {boolean} props.clearOnAction - Whether to clear input after action (default: true)
 * @param {boolean} props.showError - Whether to show error messages (default: true)
 * @param {Object} props.restProps - Additional props for the container
 */
export const TextInputWithAction = ({
  value = "",
  onChange,
  onAction,
  placeholder = "Enter text...",
  disabled = false,
  id = "text-input-with-action",
  actionButtonIcon = "Plus",
  actionButtonLabel = "Add",
  inputProps = {},
  validateInput,
  clearOnAction = true,
  showError = true,
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Update internal state when controlled value changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle input change
  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Validate input if validation function is provided
      if (validateInput) {
        const validationError = validateInput(newValue);
        setError(validationError);
      } else {
        setError(null);
      }

      if (onChange) {
        onChange(newValue, id);
      }
    },
    [onChange, id, validateInput]
  );

  // Handle action button click or Enter key press
  const handleAction = useCallback(() => {
    if (onAction && inputValue.trim()) {
      // Validate input before proceeding
      if (validateInput) {
        const validationError = validateInput(inputValue);
        if (validationError) {
          setError(validationError);
          return;
        }
      }

      onAction(inputValue.trim());

      if (clearOnAction) {
        setInputValue(""); // Clear input after action
        setError(null); // Clear errors after successful action
      }
    }
  }, [onAction, inputValue, validateInput, clearOnAction]);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleAction();
      }
    },
    [handleAction]
  );

  // Check if action should be enabled
  const isActionEnabled =
    !disabled &&
    inputValue.trim().length > 0 &&
    (validateInput ? !validateInput(inputValue) : !error);

  const ActionBtn = ButtonPrimary;

  const inputElement = (
    <InputWithActionContainer disabled={disabled} {...restProps}>
      <StyledInput
        ref={inputRef}
        id={id}
        type="text"
        className="UI_BODY_SEMIBOLD_SM"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        autoComplete="off"
        {...inputProps}
      />
      <ActionButtonContainer>
        <ActionBtn
          disabled={!inputValue}
          icon={actionButtonIcon}
          onClick={handleAction}
          disabled={!isActionEnabled}
          style={{ height: "2.4rem", width: "2.4rem" }}
        />
      </ActionButtonContainer>
    </InputWithActionContainer>
  );

  const hasError = showError && error;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <StyledErrorOuterWrapper hasError={hasError}>
        {inputElement}
        <AnimatePresence initial={false}>
          {hasError && (
            <StyledErrorContentWrapper
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: "spring", bounce: 0 }}
            >
              <StyledErrorContent>
                <UI_CAPTION_SEMIBOLD_DF>{error}</UI_CAPTION_SEMIBOLD_DF>
              </StyledErrorContent>
            </StyledErrorContentWrapper>
          )}
        </AnimatePresence>
      </StyledErrorOuterWrapper>
    </div>
  );
};
